import type { TopLevelSpec } from "vega-lite";
import type {
  BaseChartSpecOptions,
  ChartData,
  FieldType,
  StackMode,
} from "../types.js";
import {
  numberFormatConfig,
  numberFormatProps,
  type NumberFormat,
} from "./format.js";
import {
  buildTitle,
  categoryAxisLabels,
  fieldRef,
  inlineData,
} from "./shared.js";

export interface BarChartSpecOptions extends BaseChartSpecOptions {
  /** Field for the category (x) axis. */
  x: string;
  /** Field for the value (y) axis. */
  y: string;
  xType?: FieldType;
  yType?: FieldType;
  /** Field that splits bars into a colored series. */
  color?: string;
  colorType?: FieldType;
  /** Render bars horizontally, swapping the category and value axes. */
  horizontal?: boolean;
  /**
   * How a multi-series (colored) chart stacks. `true` stacks to totals,
   * `"normalize"` stacks to 100%, `false` renders grouped bars. Ignored when
   * there is no `color` series.
   */
  stack?: StackMode;
  xTitle?: string;
  yTitle?: string;
  /**
   * Draw the categories in the order of the data (the caller sorted them, or
   * took a top N) rather than sorting them by name.
   */
  keepOrder?: boolean;
  /** How values read on the value axis, the value labels and the tooltip. */
  valueFormat?: NumberFormat;
  /** How values read on the value axis, when terser than `valueFormat` (compact currency). */
  axisFormat?: NumberFormat;
  /** Write each bar's value at its end. */
  valueLabels?: boolean;
  /** Gridlines across the value axis. On by default. */
  grid?: boolean;
}

type StackValue = "zero" | "normalize" | null;

function stackModeOf(color: string | undefined, stack: StackMode): StackValue {
  if (!color) return null;
  if (stack === "normalize") return "normalize";
  return stack ? "zero" : null;
}

/** The value label layer: each bar's value just past its end. */
function valueLabelLayer(
  value: Record<string, unknown>,
  horizontal: boolean,
  valueFormat: NumberFormat | undefined,
) {
  return {
    mark: horizontal
      ? { type: "text" as const, align: "left" as const, dx: 3 }
      : { type: "text" as const, baseline: "bottom" as const, dy: -3 },
    encoding: {
      text: { ...value, ...numberFormatProps(valueFormat) },
    },
  };
}

interface FieldEncoding {
  field: string;
  type: FieldType;
  title?: string;
}

function withTitle<E extends object>(
  encoding: E,
  title: string | undefined,
): E & { title?: string } {
  return title === undefined ? encoding : { ...encoding, title };
}

/**
 * The category axis: in the data's order when kept, its labels cut to their
 * room when it runs across the plot.
 */
function categoryEncoding(
  base: FieldEncoding,
  options: {
    data: ChartData;
    name: string;
    keepOrder: boolean;
    across: boolean;
  },
) {
  return {
    ...base,
    ...(options.keepOrder ? { sort: null } : {}),
    ...(options.across
      ? { axis: categoryAxisLabels(options.data, options.name) }
      : {}),
  };
}

/** The value axis: its gridlines, number format and stacking. */
function valueEncoding(
  base: FieldEncoding,
  grid: boolean,
  valueFormat: NumberFormat | undefined,
  stackMode: StackValue,
) {
  return {
    ...base,
    axis: { grid, ...numberFormatProps(valueFormat) },
    ...(stackMode !== null ? { stack: stackMode } : {}),
  };
}

/** Grouped bars sit side by side within their category. */
function offsetEncoding(
  colorField: FieldEncoding | null,
  grouped: boolean,
  horizontal: boolean,
) {
  if (!grouped || !colorField) return {};
  return horizontal ? { yOffset: colorField } : { xOffset: colorField };
}

function tooltipOf(
  category: FieldEncoding,
  colorField: FieldEncoding | null,
  value: FieldEncoding,
  valueFormat: NumberFormat | undefined,
) {
  return [
    { field: category.field, type: category.type, title: category.title },
    ...(colorField ? [colorField] : []),
    {
      field: value.field,
      type: value.type,
      title: value.title,
      ...numberFormatProps(valueFormat),
    },
  ];
}

/** The bars, and their value labels when asked for. */
function barMarks(
  valueLabels: boolean,
  value: FieldEncoding,
  horizontal: boolean,
  valueFormat: NumberFormat | undefined,
) {
  const bar = { mark: { type: "bar" as const } };
  if (!valueLabels) return bar;
  return {
    layer: [
      bar,
      valueLabelLayer(
        { field: value.field, type: value.type },
        horizontal,
        valueFormat,
      ),
    ],
  };
}

/** Builds a Vega-Lite bar chart spec. */
export function barChartSpec(options: BarChartSpecOptions): TopLevelSpec {
  const { horizontal = false } = options;
  const {
    data,
    x,
    y,
    color,
    colorType = "nominal",
    xType = horizontal ? "quantitative" : "nominal",
    yType = horizontal ? "nominal" : "quantitative",
    stack = true,
    xTitle,
    yTitle,
    title,
    subtitle,
    keepOrder = false,
    valueFormat,
    axisFormat = valueFormat,
    valueLabels = false,
    grid = true,
  } = options;

  const stackMode = stackModeOf(color, stack);
  const xBase = withTitle({ field: fieldRef(x), type: xType }, xTitle);
  const yBase = withTitle({ field: fieldRef(y), type: yType }, yTitle);
  const [categoryBase, valueBase, categoryName] = horizontal
    ? [yBase, xBase, y]
    : [xBase, yBase, x];
  const category = categoryEncoding(categoryBase, {
    data,
    name: categoryName,
    keepOrder,
    across: !horizontal,
  });
  const value = valueEncoding(valueBase, grid, axisFormat, stackMode);
  const colorField = color ? { field: fieldRef(color), type: colorType } : null;
  const titleValue = buildTitle(title, subtitle);

  return {
    data: inlineData(data),
    ...barMarks(valueLabels, valueBase, horizontal, valueFormat),
    encoding: {
      x: horizontal ? value : category,
      y: horizontal ? category : value,
      ...(colorField ? { color: colorField } : {}),
      ...offsetEncoding(colorField, stackMode === null, horizontal),
      tooltip: tooltipOf(categoryBase, colorField, valueBase, valueFormat),
    },
    ...(titleValue !== undefined ? { title: titleValue } : {}),
    ...numberFormatConfig(valueFormat, axisFormat),
  };
}
